import { titleCase } from "@gweb/ui";
import type { Account, Contact, Opportunity, PipelineConfig } from "./types.js";

/**
 * What the UI shows and hides. The SERVER decides every move: these helpers only avoid offering what a role cannot do,
 * and they take their rules from GET /v1/pipeline so they cannot disagree with the server about what a step needs.
 */
export const SALES_READ_ROLES = ["gweb_sales", "gweb_sales_ops", "gweb_executive", "gweb_admin"];
export const SALES_WRITE_ROLES = ["gweb_sales", "gweb_sales_ops", "gweb_admin"];

const intersects = (a: readonly string[], b: readonly string[]): boolean => a.some((x) => b.includes(x));

export const GATE_LABELS: Record<string, string> = {
  account_industry_country: "The account has an industry and a country",
  has_contact: "The account has at least one contact",
  contact_verified: "A contact is verified, with a business email and not marked do-not-contact",
  owner_set: "The opportunity has an owner",
  products_attached: "At least one product is attached",
  decision_maker_mapped: "A decision-maker contact is mapped",
  handoff_package_valid: "The handoff details are complete (primary contact and locations)",
  handoff_package_present: "A handoff package was issued at Closed Won",
};
export const gateLabel = (g: string): string => GATE_LABELS[g] ?? titleCase(g);
export const evidenceLabel = (e: string): string => titleCase(e);
export const stageLabel = (s: string): string => titleCase(s);

export type GateStatus = "met" | "unmet" | "unknown";

/** What the loaded data already says about a gate. "unknown" means only the server can tell. */
export function gateStatus(gate: string, ctx: { account: Account; contacts: Contact[]; opp: Opportunity }): GateStatus {
  const { account, contacts, opp } = ctx;
  switch (gate) {
    case "account_industry_country": return account.industry && account.country ? "met" : "unmet";
    case "has_contact": return contacts.length > 0 ? "met" : "unmet";
    case "contact_verified": return contacts.some((c) => c.business_email && c.last_verified_at && !c.do_not_contact) ? "met" : "unmet";
    case "owner_set": return opp.owner ? "met" : "unmet";
    case "products_attached": return opp.products.length > 0 ? "met" : "unmet";
    case "decision_maker_mapped": return contacts.some((c) => c.buying_role === "decision-maker") ? "met" : "unmet";
    case "handoff_package_present": return opp.handoff_package_id ? "met" : "unmet";
    default: return "unknown";
  }
}

export type MoveKind = "forward" | "skip" | "rollback" | "side" | "reopen";
export interface Move {
  id: string; kind: MoveKind; to: string; label: string;
  /** False means the role may not make this move; it is shown disabled only for the forward step, to explain why. */
  allowed: boolean; why?: string;
  needs: { reason: boolean; evidence: string[]; gates: string[]; closedReason: boolean; reviewDate: boolean };
}

const numberOf = (cfg: PipelineConfig, code: string): number => cfg.stages.find((s) => s.code === code)?.number ?? 0;
const codeAt = (cfg: PipelineConfig, n: number): string | undefined => cfg.stages.find((s) => s.number === n)?.code;
const NONE = { reason: false, evidence: [] as string[], gates: [] as string[], closedReason: false, reviewDate: false };

function rolesLabel(roles: string[]): string {
  return roles.includes("gweb_sales") ? "Sales" : "Sales Ops";
}

/** The moves this role may make from this stage. The forward step is always listed, disabled when the role cannot make it. */
export function movesFor(stage: string, cfg: PipelineConfig, roles: string[]): Move[] {
  const out: Move[] = [];
  const n = numberOf(cfg, stage);
  const r = cfg.rules;

  if (n > 0) {
    const rule = cfg.forward.find((f) => f.from === stage);
    if (rule) {
      const allowed = intersects(roles, rule.roles);
      out.push({ id: `forward:${rule.to}`, kind: "forward", to: rule.to, label: `Advance to ${stageLabel(rule.to)}`, allowed, ...(allowed ? {} : { why: `Only ${rolesLabel(rule.roles)} can make this move` }), needs: { ...NONE, evidence: rule.evidence, gates: rule.gates } });
    }
    if (intersects(roles, r.bulk_advance.roles)) {
      for (let j = n + 2; j <= cfg.stages.length; j++) {
        const to = codeAt(cfg, j) as string;
        const path = cfg.forward.filter((f) => numberOf(cfg, f.from) >= n && numberOf(cfg, f.to) <= j);
        out.push({ id: `skip:${to}`, kind: "skip", to, label: `Skip ahead to ${stageLabel(to)}`, allowed: true,
          needs: { ...NONE, reason: true, evidence: [...new Set(path.flatMap((p) => p.evidence))], gates: [...new Set(path.flatMap((p) => p.gates))] } });
      }
    }
    const prev = codeAt(cfg, n - 1);
    if (prev && !r.rollback.blocked_into.includes(prev) && intersects(roles, r.rollback.roles)) {
      out.push({ id: `rollback:${prev}`, kind: "rollback", to: prev, label: `Roll back to ${stageLabel(prev)}`, allowed: true, needs: { ...NONE, reason: true } });
    }
    for (const s of cfg.side_states) {
      if (s === "closed-lost" && n > numberOf(cfg, "onboarding")) continue; // not possible once onboarding has finished
      const signed = n >= numberOf(cfg, "closed-won");
      const roleList = s === "closed-lost" && signed ? r.side_state.cancel_after_signature_roles : r.side_state.roles;
      if (!intersects(roles, roleList)) continue;
      out.push({ id: `side:${s}`, kind: "side", to: s, label: s === "closed-lost" ? "Mark as Closed Lost" : `Move to ${stageLabel(s)}`, allowed: true,
        needs: { ...NONE, reason: true, closedReason: s === "closed-lost", reviewDate: s === "nurture" } });
    }
    return out;
  }

  // a side state: it can be re-opened early in the pipeline, and nurture or dormant can be disqualified
  const roleList = stage === "disqualified" ? r.reopen.from_disqualified_roles : r.reopen.roles;
  if (intersects(roles, roleList)) {
    for (let j = 1; j <= r.reopen_max_stage; j++) {
      const to = codeAt(cfg, j) as string;
      out.push({ id: `reopen:${to}`, kind: "reopen", to, label: `Re-open at ${stageLabel(to)}`, allowed: true, needs: { ...NONE, reason: true, evidence: ["new_trigger"] } });
    }
  }
  if ((stage === "nurture" || stage === "dormant") && intersects(roles, r.side_state.roles)) {
    out.push({ id: "side:disqualified", kind: "side", to: "disqualified", label: "Disqualify", allowed: true, needs: { ...NONE, reason: true } });
  }
  return out;
}

export type Tone = "neutral" | "accent" | "success" | "warn" | "danger";
export function stageTone(stage: string, cfg: PipelineConfig): Tone {
  if (stage === "disqualified" || stage === "closed-lost") return "danger";
  if (stage === "nurture" || stage === "dormant") return "neutral";
  const n = numberOf(cfg, stage);
  if (n >= 11) return "success";
  if (n >= 8) return "warn";
  if (n >= 5) return "accent";
  return "neutral";
}

export const isSideState = (cfg: PipelineConfig, s: string): boolean => cfg.side_states.includes(s);
export { numberOf };
